<template>
    <div class="mt-2" v-if="canView">
        <div v-if="!showBuilder && !showWizard">
            <div class="flex justify-between items-center mb-2">
                <h2 class="text-lg font-semibold">Result Templates</h2>
                <div class="flex gap-3">
                    <div class="w-64">
                        <AcademicCalendarSelect v-model="selectedAcademicCalendar"
                            placeholder="Select Academic Calendar" />
                    </div>
                    <button @click="openWizard()" v-if="canCreate"
                        data-testid="btn-create-new-template"
                        class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90">
                        Create New Template
                    </button>
                </div>
            </div>

            <div v-if="loading" class="text-center py-4">Loading templates...</div>
            <div v-else-if="error" class="text-center py-4 text-red-500">Error loading templates: {{ error.message }}
            </div>
            <div v-else>
                <DataTable size="small" :value="templates" v-model:expandedRows="expandedRows" dataKey="id"
                    tableStyle="min-width: 50rem" :paginator="true" :rows="10">
                    <Column expander style="width: 5rem" />
                    <Column field="name" header="Name" sortable></Column>
                    <Column field="code" header="Code" sortable></Column>
                    <Column field="grade_group.name" header="Grade Group" sortable>
                        <template #body="slotProps">
                            {{ slotProps.data.grade_group?.name || 'N/A' }}
                        </template>
                    </Column>
                    <Column field="created_at" header="Created At" sortable>
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.created_at) }}
                        </template>
                    </Column>
                    <Column header="Actions">
                        <template #body="slotProps">
                            <div class="flex space-x-2">
                                <button @click="openBuilder(slotProps.data.id)" v-if="canUpdate"
                                    data-testid="btn-edit-template"
                                    class="text-primary hover:text-primary-700">Edit</button>
                                <button @click="openAssignModal(slotProps.data)" v-if="canUpdate"
                                    data-testid="btn-assign-template"
                                    class="text-purple-600 hover:text-purple-800">
                                    {{ isTertiary ? 'Assign to Programme' : 'Assign to Class' }}
                                </button>
                                <button @click="confirmDelete(slotProps.data)" v-if="canDelete"
                                    data-testid="btn-delete-template"
                                    :disabled="deletingId === slotProps.data.id"
                                    class="text-red-600 hover:text-red-800 disabled:opacity-50">
                                    {{ deletingId === slotProps.data.id ? 'Deleting…' : 'Delete' }}
                                </button>
                            </div>
                        </template>
                    </Column>

                    <template #expansion="slotProps">
                        <div class="p-2.5 bg-gray-50 rounded-md">
                            <!-- For primary/secondary schools: show classrooms -->
                            <template v-if="!isTertiary">
                                <h4 class="font-semibold mb-2 text-sm text-gray-700">Assigned Classrooms</h4>
                                <div v-if="slotProps.data.classrooms && slotProps.data.classrooms.length > 0"
                                    class="flex flex-wrap gap-2">
                                    <span v-for="classroom in slotProps.data.classrooms" :key="classroom.id"
                                        class="px-2 py-1 bg-white border border-gray-200 rounded text-sm text-gray-800 shadow-sm">
                                        {{ classroom.name }}
                                    </span>
                                </div>
                                <div v-else class="text-sm text-gray-500 italic">
                                    Not assigned to any classrooms.
                                </div>
                            </template>
                            <!-- For tertiary schools: show programmes -->
                            <template v-else>
                                <h4 class="font-semibold mb-2 text-sm text-gray-700">
                                    {{ selectedAcademicCalendar ? 'Assigned for this semester' : 'Assigned Programmes' }}
                                </h4>
                                <div v-if="slotProps.data.programmes && slotProps.data.programmes.length > 0"
                                    class="flex flex-wrap gap-2" data-testid="template-assigned-programmes">
                                    <span v-for="programme in slotProps.data.programmes" :key="programme.id"
                                        class="inline-flex items-center gap-1.5 px-2 py-1 bg-primary/10 border border-primary/20 rounded text-sm text-primary-700 shadow-sm">
                                        {{ programmeChipLabel(programme) }}
                                        <button
                                            class="pi pi-times text-[10px] text-primary hover:text-red-500 transition-colors"
                                            v-tooltip="'Unassign from programme'"
                                            :aria-label="`Unassign from ${programme.program_name}`"
                                            @click.stop="confirmUnassign(slotProps.data, programme)"
                                        ></button>
                                    </span>
                                </div>
                                <div v-else class="text-sm text-gray-500 italic">
                                    {{ selectedAcademicCalendar ? 'No programme has this template for this semester.' : 'Not assigned to any programmes.' }}
                                </div>

                                <template v-if="slotProps.data.default_for_programmes?.length">
                                    <h4 class="font-semibold mt-3 mb-2 text-sm text-gray-700">Default for all semesters</h4>
                                    <div class="flex flex-wrap gap-2" data-testid="template-default-programmes">
                                        <span v-for="programme in slotProps.data.default_for_programmes" :key="programme.id"
                                            class="inline-flex items-center gap-1.5 px-2 py-1 bg-surface-0 border border-dashed border-primary/40 rounded text-sm shadow-sm">
                                            <i class="pi pi-star text-[10px] text-primary" aria-hidden="true"></i>
                                            {{ programmeChipLabel(programme) }}
                                            <button
                                                class="pi pi-times text-[10px] text-primary hover:text-red-500 transition-colors"
                                                v-tooltip="'Stop using as default'"
                                                :aria-label="`Stop using as default for ${programme.program_name}`"
                                                :data-testid="`btn-remove-default-${programme.id}`"
                                                @click.stop="confirmRemoveDefault(slotProps.data, programme)"
                                            ></button>
                                        </span>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>

        <ResultTemplateWizard v-else-if="showWizard" @close="showWizard = false"
            @saved="handleSaved" @switch-to-builder="switchToBuilder" />

        <ResultTemplateBuilder v-else :templateId="selectedTemplateId" @close="showBuilder = false"
            @saved="handleSaved" />

        <!-- Classroom assignment modal (primary/secondary) -->
        <AssignTemplateModal v-if="showAssignModal && !isTertiary" :template="selectedTemplateForAssign"
            @close="showAssignModal = false" @assigned="handleAssigned" />

        <!-- Programme assignment modal (tertiary) -->
        <AssignTemplateToProgrammeModal v-if="showAssignModal && isTertiary" :template="selectedTemplateForAssign"
            @close="showAssignModal = false" @assigned="handleAssigned" />
    </div>
</template>

<script setup lang="ts">
import { useResultTemplatesQuery } from '@/types/vue-apollo-helpers';
import type { ResultTemplate } from '@/types/composition-functions';
import ResultTemplateBuilder from './ResultTemplateBuilder.vue';
import ResultTemplateWizard from './ResultTemplateWizard.vue';
import AssignTemplateModal from './AssignTemplateModal.vue';
import AssignTemplateToProgrammeModal from './AssignTemplateToProgrammeModal.vue';

import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';
import { useResultTemplateMutations } from '@/composables/mutations/useResultTemplateMutations';
import { useDebugMode } from '@/composables/useDebugMode';
import { programmeChipLabel } from '@/utils/programmeTemplateAssignment';

const selectedAcademicCalendar = ref<string | undefined>(undefined);
const page = ref(1);

// Check if school is tertiary
const { isTertiary } = useSchoolFeatures();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const { enabled: debugEnabled } = useDebugMode();

const canView = computed(() => can(currentUser.value, AppPermissions.ResultTemplates.Actions.View, AppPermissions.ResultTemplates.Resource));
const canCreate = computed(() => can(currentUser.value, AppPermissions.ResultTemplates.Actions.Create, AppPermissions.ResultTemplates.Resource));
const canUpdate = computed(() => can(currentUser.value, AppPermissions.ResultTemplates.Actions.Update, AppPermissions.ResultTemplates.Resource));
// Delete is gated behind the hidden debug flag in addition to the permission.
const canDelete = computed(() => debugEnabled.value && can(currentUser.value, AppPermissions.ResultTemplates.Actions.Delete, AppPermissions.ResultTemplates.Resource));

const { result, loading, error, refetch } = useResultTemplatesQuery(() => ({
  page: page.value,
  first: 10,
  include_programmes: isTertiary.value,
  academic_calendar_id: selectedAcademicCalendar.value,
}), { enabled: canView.value });

const templates = computed(() => result.value?.resultTemplates?.data || []);
const showBuilder = ref(false);
const showWizard = ref(false);
const selectedTemplateId = ref<string | null>(null);

const showAssignModal = ref(false);
const selectedTemplateForAssign = ref<ResultTemplate | null>(null);

const expandedRows = ref({});

// Auto-expand all rows when data is loaded
watch(templates, (newTemplates) => {
  if (newTemplates && newTemplates.length > 0) {
    // Expand all by default
    const expanded: Record<string, boolean> = {};
    newTemplates.forEach((t) => {
      expanded[t.id] = true;
    });
    expandedRows.value = expanded;
  }
}, { immediate: true });

function formatDate(dateString: string) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
}

function openBuilder(id: string | null = null) {
  selectedTemplateId.value = id;
  showBuilder.value = true;
}

function openWizard() {
  showWizard.value = true;
}

function switchToBuilder() {
  showWizard.value = false;
  openBuilder();
}

function openAssignModal(template: ResultTemplate) {
  selectedTemplateForAssign.value = template;
  showAssignModal.value = true;
}

function handleSaved() {
  refetch();
  showBuilder.value = false;
  showWizard.value = false;
}

function handleAssigned() {
  refetch(); // Refresh to show new assignments in the expanded row
  showAssignModal.value = false;
}

// --- Mutations (via composable) ---
const { deletingId, confirmUnassign: _confirmUnassign, confirmRemoveDefault, confirmDelete } = useResultTemplateMutations({ refetch });

function confirmUnassign(template: ResultTemplate, programme: { id: string; program_name: string }) {
  _confirmUnassign(template, programme, selectedAcademicCalendar.value);
}
</script>

